import type { ReactNode } from 'react'

/**
 * Round 36px (`h-9`) icon button used by every control in the article header
 * (share, print). `group` scopes the hover tooltip below to this specific
 * button rather than to every `.group` on the page.
 */
const readIconActionClass =
  'group relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-input bg-background text-foreground/70 transition-colors hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background print:hidden'

const tooltipClass =
  'pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-2 py-1 text-xs font-medium text-background opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100'

interface ReadIconActionProps {
  /** Accessible name, also shown as the hover/focus tooltip. */
  label: string
  /** The icon itself — sized by the caller (`h-4 w-4` for lucide icons). */
  children: ReactNode
  /** Renders an external link when set, a plain button otherwise. */
  href?: string
  onClick?: () => void
}

/**
 * Icon-only header action. The label is never rendered as visible button
 * text: it is exposed as the element's `aria-label` (so assistive tech and
 * the browser's own tooltip always have it) and revealed visually only on
 * hover or keyboard focus, via a CSS-only tooltip.
 */
export function ReadIconAction({ label, children, href, onClick }: ReadIconActionProps) {
  const tooltip = (
    <span aria-hidden="true" className={tooltipClass}>
      {label}
    </span>
  )

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        title={label}
        className={readIconActionClass}
      >
        {children}
        {tooltip}
      </a>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={readIconActionClass}
    >
      {children}
      {tooltip}
    </button>
  )
}
